import React, { useEffect, useState } from "react";
import SectionShell from "./SectionShell";
import Breadcrumbs from "./Breadcrumbs";
import { useBrand } from "../../branding/BrandContext";
import { BrandMark, getBrandedPlainText } from "../../branding/BrandText";

function getFallbackHeadline(mode, brand) {
  if (mode === "detecting") {
    return "Checking your approximate location";
  }

  if (mode === "confirmed") {
    return getBrandedPlainText("Your Home-Helper service region is ready", brand);
  }

  if (mode === "unavailable") {
    return "We need your ZIP code";
  }

  return "Enter your ZIP code";
}

export default function HeroZip({
  hero,
  breadcrumbs,
  mode = "detecting",
  regionName = "",
  zipValue = "",
  loading = false,
  errorMessage = "",
  helperMessage = "",
  questionText = "",
  onSubmit,
  onZipSubmit,
}) {
  const brand = useBrand();
  const h = hero || {};
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [manualZip, setManualZip] = useState(zipValue || "");
  const [selection, setSelection] = useState("");
  const safeRegionName = String(regionName || "").trim();

  useEffect(() => {
    setManualZip(zipValue || "");
  }, [zipValue]);

  useEffect(() => {
    setSelection("");
  }, [mode, safeRegionName]);

  const handleZipSubmit = (event) => {
    event.preventDefault();

    if (typeof onZipSubmit === "function") {
      onZipSubmit(manualZip);
    }
  };

  const handleProximitySubmit = (event) => {
    event.preventDefault();

    if (!selection) {
      return;
    }

    if (selection === "no") {
      if (typeof onZipSubmit === "function") {
        onZipSubmit(manualZip);
      }
      return;
    }

    if (typeof onSubmit !== "function") {
      return;
    }

    onSubmit({
      answer: selection,
      lives_within_region_drive_time: selection === "yes",
      region_slug: null,
      region_name_snapshot: safeRegionName || null,
      region_proximity_question: questionText || null,
    });
  };

  const handleSelectionChange = (nextSelection) => {
    setSelection(nextSelection);

    if (nextSelection !== "yes" || loading || typeof onSubmit !== "function") {
      return;
    }

    onSubmit({
      answer: "yes",
      lives_within_region_drive_time: true,
      region_slug: null,
      region_name_snapshot: safeRegionName || null,
      region_proximity_question: questionText || null,
    });
  };

  let locationCard = null;

  if (mode === "detecting") {
    locationCard = (
      <div className="cl-locationStateCard cl-locationStateCard--fullWidth">
        <div className="cl-locationSpinner" aria-hidden="true" />
        <div className="cl-locationStateTitle">
          {getFallbackHeadline(mode, brand)}
        </div>
        <div className="cl-locationStateCopy">
          {helperMessage ||
            "We use your approximate location to help match you with the right local service region. You can change it anytime."}
        </div>
      </div>
    );
  } else if (mode === "proximity") {
    locationCard = (
      <div className="cl-locationStateCard cl-locationStateCard--fullWidth">
        <div className="cl-locationEyebrow">Service region match</div>
        <div className="cl-proximityPrompt">{questionText}</div>
        <div className="cl-locationStateCopy">
          {helperMessage ||
            getBrandedPlainText(
              "We use your approximate location to match you with the closest Home-Helper service region.",
              brand
            )}
        </div>
        <form className="cl-zipForm" onSubmit={handleProximitySubmit} noValidate>
          <div className="cl-zipRow">
            {selection === "no" ? (
              <input
                className="cl-zipInput cl-zipInput--manualEntry"
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={5}
                placeholder="Enter ZIP"
                value={manualZip}
                onChange={(event) =>
                  setManualZip(event.target.value.replace(/\D/g, "").slice(0, 5))
                }
              />
            ) : (
              <div
                className="cl-proximityOptions"
                role="radiogroup"
                aria-label={questionText}
              >
                {[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ].map((option) => {
                  const checked = selection === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`cl-proximityOption ${checked ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="region-proximity"
                        value={option.value}
                        checked={checked}
                        onChange={(event) => handleSelectionChange(event.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            )}

            <button
              className="cl-zipButton"
              type="submit"
              disabled={
                loading ||
                !selection ||
                (selection === "no" && String(manualZip || "").trim().length !== 5)
              }
            >
              {loading ? "Checking..." : "Continue"}
            </button>
          </div>
        </form>

      </div>
    );
  } else if (mode === "zip" || mode === "unavailable") {
    locationCard = (
      <div className="cl-locationStateCard cl-locationStateCard--fullWidth">
        <div className="cl-locationStateTitle">
          {getFallbackHeadline(mode, brand)}
        </div>
        <div className="cl-locationStateCopy">
          {helperMessage ||
            getBrandedPlainText(
              "Enter your ZIP code so we can match you with the right Home-Helper service region.",
              brand
            )}
        </div>

        <form className="cl-zipForm" onSubmit={handleZipSubmit} noValidate>
          <div className="cl-zipRow cl-zipRow--stackMobile">
            <input
              className="cl-zipInput"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              placeholder="ZIP code"
              value={manualZip}
              onChange={(event) =>
                setManualZip(event.target.value.replace(/\D/g, "").slice(0, 5))
              }
            />
            <button className="cl-zipButton" type="submit" disabled={loading}>
              {loading ? "Checking..." : "Find My Region"}
            </button>
          </div>
        </form>

      </div>
    );
  }

  return (
    <div
      className="cl-hero"
      style={
        h.backgroundImage
          ? { backgroundImage: `url(${h.backgroundImage})` }
          : undefined
      }
    >
      <SectionShell className="cl-heroInner">
        <div className="cl-heroGrid">
          <div className="cl-heroTopRow">
            <div className="cl-heroCopyColumn">
              <div className="cl-heroCopy">
              {breadcrumbs?.length ? <Breadcrumbs items={breadcrumbs} /> : null}

              {h.title ? <h1 className="cl-heroTitle">{h.title}</h1> : null}

              {h.subtitle ? (
                <div className="cl-heroSubtitle">
                  <div>{h.subtitle}</div>
                </div>
              ) : null}
              </div>

              {errorMessage ? (
                <div className="cl-zipError cl-heroRowError" role="alert">
                  {errorMessage}
                </div>
              ) : null}

              {locationCard ? <div className="cl-heroInlineCard">{locationCard}</div> : null}
            </div>

            <div className="cl-heroMediaColumn">
              {h.illustrationImage ? (
                <div className="cl-heroArt">
                  <img
                    src={h.illustrationImage}
                    alt={h.illustrationAlt || ""}
                    className="cl-heroImg"
                  />
                </div>
              ) : null}

              <button
                className="cl-helpButtonContainer cl-helpButtonContainer--media"
                onClick={() => setShowHelpModal(true)}
                type="button"
              >
                How does this work?
              </button>

              {h.disclaimer ? (
                <div className="cl-heroDisclaimer cl-heroDisclaimer--media">
                  {h.disclaimer}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </SectionShell>

      {showHelpModal && (
        <div
          className="heroModalOverlay"
          onClick={() => setShowHelpModal(false)}
        >
          <div
            className="heroModalContent"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>How Does This Work?</h3>

            <p>
              <BrandMark /> uses your photos to analyze your project area and provide a detailed
              summary of its current condition, along with personalized recommendations for
              improvements. This helps you understand the scope of your project and make informed
              decisions before connecting with local professionals.
            </p>

            <h3>Before You Begin</h3>

            <p>
              To get the most accurate estimate, please take <strong>one to three clear photos</strong>
              of your project area before starting. Include wide angles that show the full space and
              close-ups of key features you want to improve. This helps our technology generate a
              more precise and helpful summary report.
            </p>

            <button
              className="heroModalClose"
              onClick={() => setShowHelpModal(false)}
              type="button"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
